// Thumbnail Generation Utility
// Phase 53 - Attachment Thumbnail Previews
// Generates 200x200 PNG thumbnails for images and PDFs client-side

import * as pdfjs from 'pdfjs-dist';

// Configure PDF.js worker - use local copy from public directory
// Same configuration as PDFViewer.tsx for consistency
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Maximum thumbnail dimensions (preserves aspect ratio)
const MAX_THUMBNAIL_SIZE = 200;

// Supported MIME types for thumbnail generation
const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];

const SUPPORTED_PDF_TYPES = ['application/pdf'];

const ALL_SUPPORTED_TYPES = [...SUPPORTED_IMAGE_TYPES, ...SUPPORTED_PDF_TYPES];

/**
 * Check if a thumbnail can be generated for the given MIME type
 */
export function canGenerateThumbnail(mimeType: string): boolean {
  return ALL_SUPPORTED_TYPES.includes(mimeType);
}

/**
 * Generate a 200x200 PNG thumbnail for an image or PDF file
 * Returns null for unsupported types (doesn't throw)
 */
export async function generateThumbnail(file: File): Promise<Blob | null> {
  try {
    if (SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      return await generateImageThumbnail(file);
    }

    if (SUPPORTED_PDF_TYPES.includes(file.type)) {
      return await generatePDFThumbnail(file);
    }

    // Unsupported type
    return null;
  } catch (error) {
    console.warn('Thumbnail generation failed:', error);
    return null;
  }
}

/**
 * Generate thumbnail for image files (JPEG, PNG, WebP, GIF)
 */
async function generateImageThumbnail(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    // Create object URL from File directly to avoid CORS issues
    const objectUrl = URL.createObjectURL(file);

    const img = new Image();

    img.onload = () => {
      try {
        // Calculate scaled dimensions preserving aspect ratio
        const { width, height } = calculateScaledDimensions(
          img.naturalWidth,
          img.naturalHeight,
          MAX_THUMBNAIL_SIZE
        );

        // Create canvas and draw scaled image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(objectUrl);
          resolve(null);
          return;
        }

        // Draw image to canvas at scaled dimensions
        ctx.drawImage(img, 0, 0, width, height);

        // Export as PNG blob
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            resolve(blob);
          },
          'image/png',
          0.9
        );
      } catch {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };

    img.src = objectUrl;
  });
}

/**
 * Generate thumbnail for PDF files (renders first page)
 */
async function generatePDFThumbnail(file: File): Promise<Blob | null> {
  try {
    // Convert File to ArrayBuffer for pdfjs
    const arrayBuffer = await file.arrayBuffer();

    // Load PDF document
    const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;

    // Get first page
    const page = await pdf.getPage(1);

    // Calculate viewport scale to fit within MAX_THUMBNAIL_SIZE
    const originalViewport = page.getViewport({ scale: 1 });
    const scale = Math.min(
      MAX_THUMBNAIL_SIZE / originalViewport.width,
      MAX_THUMBNAIL_SIZE / originalViewport.height
    );

    const viewport = page.getViewport({ scale });

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    // Render page to canvas
    const renderContext = {
      canvasContext: ctx,
      viewport: viewport,
    };

    await page.render(renderContext).promise;

    // Export as PNG blob
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/png',
        0.9
      );
    });
  } catch (error) {
    console.warn('PDF thumbnail generation failed:', error);
    return null;
  }
}

/**
 * Calculate dimensions to fit within maxSize while preserving aspect ratio
 */
function calculateScaledDimensions(
  originalWidth: number,
  originalHeight: number,
  maxSize: number
): { width: number; height: number } {
  // If image is smaller than max size, return original dimensions
  if (originalWidth <= maxSize && originalHeight <= maxSize) {
    return { width: originalWidth, height: originalHeight };
  }

  // Calculate scale factor to fit within maxSize
  const scale = Math.min(maxSize / originalWidth, maxSize / originalHeight);

  return {
    width: Math.floor(originalWidth * scale),
    height: Math.floor(originalHeight * scale),
  };
}
